import { loadImage } from "@hg/recall-frame"

import shareFrameImage from "../../../../assets/imgs/recall/share/frame.png"
import shareBgImage from "../../../../assets/imgs/recall/share/share.png"

const DESIGN_WIDTH = 1352
const DESIGN_HEIGHT = 807
const CANVAS_WIDTH = DESIGN_WIDTH * 2
const CANVAS_HEIGHT = DESIGN_HEIGHT * 2

const FONT_SANS = '"Apple SD Gothic Neo", "Malgun Gothic", sans-serif'
const FONT_NAME = '"a한글세상B", "STKaiti", serif'

const CG_PADDING = 4
const CG_RECT = {
    x: 120,
    y: 100,
    width: 721,
    height: 398,
}
const QRCODE_RECT = {
    x: 151,
    y: 538,
    width: 134,
    height: 134,
}
const NICKNAME_RECT = {
    x: 307,
    y: 559,
    width: 462,
    height: 51,
}
const CODE_RECT = {
    x: 307,
    y: 610,
    width: 462,
    height: 40,
}

type Rect = {
    x: number
    y: number
    width: number
    height: number
}

type LayoutMetrics = {
    scale: number
    offsetX: number
    offsetY: number
}

type DrawStrokeTextOptions = {
    text: string
    rect: Rect
    fontSize: number
    fontFamily: string
    fontWeight?: string
    fillColor: string
    strokeColor: string
    strokeWidth: number
    minFontSize?: number
    wrapLines?: boolean
}

export interface DrawRecallShareImageOptions {
    cg: string
    qrCodeUrl: string
    nickname: string
    code?: string
}

export interface DrawRecallShareImageResult {
    canvas: HTMLCanvasElement
    base64: string
}

const createLayoutMetrics = (): LayoutMetrics => {
    const scale = Math.min(
        CANVAS_WIDTH / DESIGN_WIDTH,
        CANVAS_HEIGHT / DESIGN_HEIGHT,
    )

    return {
        scale,
        offsetX: (CANVAS_WIDTH - DESIGN_WIDTH * scale) / 2,
        offsetY: (CANVAS_HEIGHT - DESIGN_HEIGHT * scale) / 2,
    }
}

const scaleValue = (value: number, metrics: LayoutMetrics) =>
    value * metrics.scale

const scaleRect = (rect: Rect, metrics: LayoutMetrics): Rect => ({
    x: metrics.offsetX + scaleValue(rect.x, metrics),
    y: metrics.offsetY + scaleValue(rect.y, metrics),
    width: scaleValue(rect.width, metrics),
    height: scaleValue(rect.height, metrics),
})

const createCanvas = () => {
    const canvas = document.createElement("canvas")
    canvas.width = CANVAS_WIDTH
    canvas.height = CANVAS_HEIGHT
    return canvas
}

const drawRectImage = (
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    rect: Rect,
) => {
    ctx.drawImage(image, rect.x, rect.y, rect.width, rect.height)
}

const drawCoverImage = (
    ctx: CanvasRenderingContext2D,
    image: { width: number; height: number } & CanvasImageSource,
    rect: Rect,
) => {
    const sourceAspect = image.width / image.height
    const targetAspect = rect.width / rect.height

    let sourceWidth = image.width
    let sourceHeight = image.height
    let sourceX = 0
    let sourceY = 0

    if (sourceAspect > targetAspect) {
        sourceWidth = image.height * targetAspect
        sourceX = (image.width - sourceWidth) / 2
    } else {
        sourceHeight = image.width / targetAspect
        sourceY = (image.height - sourceHeight) / 2
    }

    ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
    )
}

const loadFont = async (font: string, text: string) => {
    if (typeof document === "undefined" || !document.fonts) {
        return
    }

    try {
        if (!document.fonts.check(font, text)) {
            await document.fonts.load(font, text)
        }
    } catch {
        // Ignore font loading failures and render with the current browser state.
    }
}

const createRoundedRectPath = (
    ctx: CanvasRenderingContext2D,
    rect: Rect,
    radius: number,
) => {
    const safeRadius = Math.min(radius, rect.width / 2, rect.height / 2)

    ctx.beginPath()
    ctx.moveTo(rect.x + safeRadius, rect.y)
    ctx.arcTo(
        rect.x + rect.width,
        rect.y,
        rect.x + rect.width,
        rect.y + rect.height,
        safeRadius,
    )
    ctx.arcTo(
        rect.x + rect.width,
        rect.y + rect.height,
        rect.x,
        rect.y + rect.height,
        safeRadius,
    )
    ctx.arcTo(rect.x, rect.y + rect.height, rect.x, rect.y, safeRadius)
    ctx.arcTo(rect.x, rect.y, rect.x + rect.width, rect.y, safeRadius)
    ctx.closePath()
}

const drawRoundedImage = (
    ctx: CanvasRenderingContext2D,
    image: CanvasImageSource,
    rect: Rect,
    radius: number,
) => {
    ctx.save()
    createRoundedRectPath(ctx, rect, radius)
    ctx.clip()
    drawRectImage(ctx, image, rect)
    ctx.restore()
}

const fitFontSize = (
    ctx: CanvasRenderingContext2D,
    text: string,
    options: {
        fontFamily: string
        fontWeight?: string
        initialSize: number
        minSize: number
        maxWidth: number
    },
) => {
    const {
        fontFamily,
        fontWeight = "normal",
        initialSize,
        minSize,
        maxWidth,
    } = options

    for (let size = initialSize; size >= minSize; size -= 1) {
        ctx.font = `${fontWeight} ${size}px ${fontFamily}`
        if (ctx.measureText(text).width <= maxWidth) {
            return size
        }
    }

    return minSize
}

const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
): string[] => {
    const lines: string[] = []
    let currentLine = ""

    for (const char of Array.from(text)) {
        const nextLine = currentLine + char
        if (currentLine && ctx.measureText(nextLine).width > maxWidth) {
            lines.push(currentLine)
            currentLine = char
        } else {
            currentLine = nextLine
        }
    }

    if (currentLine) lines.push(currentLine)
    return lines.length > 0 ? lines : [""]
}

const drawStrokeText = (
    ctx: CanvasRenderingContext2D,
    metrics: LayoutMetrics,
    options: DrawStrokeTextOptions,
) => {
    const {
        text,
        rect,
        fontSize,
        fontFamily,
        fontWeight,
        fillColor,
        strokeColor,
        strokeWidth,
        wrapLines,
    } = options
    const scaledRect = scaleRect(rect, metrics)
    const scaledStrokeWidth = scaleValue(strokeWidth, metrics)
    const maxTextWidth = scaledRect.width - scaledStrokeWidth * 2

    let resolvedFontSize: number
    let lines: string[]

    if (wrapLines) {
        resolvedFontSize = Math.round(scaleValue(fontSize, metrics))
        ctx.font = `${fontWeight || "normal"} ${resolvedFontSize}px ${fontFamily}`
        lines = wrapText(ctx, text, maxTextWidth)
    } else {
        resolvedFontSize = fitFontSize(ctx, text, {
            fontFamily,
            fontWeight,
            initialSize: Math.round(scaleValue(fontSize, metrics)),
            minSize: Math.round(
                scaleValue(options.minFontSize ?? fontSize * 0.7, metrics),
            ),
            maxWidth: maxTextWidth,
        })
        lines = [text]
    }

    ctx.save()
    ctx.font = `${fontWeight || "normal"} ${resolvedFontSize}px ${fontFamily}`
    ctx.fillStyle = fillColor
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = scaledStrokeWidth * 2
    ctx.lineJoin = "round"
    ctx.lineCap = "round"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"

    const lineHeight = resolvedFontSize * 1.3
    const totalHeight = lines.length * lineHeight
    const centerX = scaledRect.x + scaledRect.width / 2
    const startY =
        scaledRect.y + scaledRect.height / 2 - totalHeight / 2 + lineHeight / 2

    lines.forEach((line, index) => {
        const y = startY + index * lineHeight
        ctx.strokeText(line, centerX, y)
        ctx.fillText(line, centerX, y)
    })

    ctx.restore()
}

const loadAssets = async (options: DrawRecallShareImageOptions) => {
    const [shareBg, shareFrame, cgImage, qrCodeImage] = await Promise.all([
        loadImage(shareBgImage),
        loadImage(shareFrameImage),
        loadImage(options.cg),
        loadImage(options.qrCodeUrl),
    ])

    return {
        shareBg,
        shareFrame,
        cgImage,
        qrCodeImage,
    }
}

export const drawRecallShareImage = async (
    options: DrawRecallShareImageOptions,
): Promise<DrawRecallShareImageResult> => {
    const canvas = createCanvas()
    const ctx = canvas.getContext("2d")

    if (!ctx) {
        throw new Error("2d canvas context is not available")
    }

    const metrics = createLayoutMetrics()
    const assets = await loadAssets(options)

    await Promise.all([
        loadFont(
            `bold ${Math.round(scaleValue(30, metrics))}px ${FONT_SANS}`,
            options.nickname,
        ),
        options.code
            ? loadFont(
                  `normal ${Math.round(scaleValue(34, metrics))}px ${FONT_NAME}`,
                  options.code,
              )
            : Promise.resolve(),
    ])

    drawRectImage(
        ctx,
        assets.shareBg,
        scaleRect(
            {
                x: 0,
                y: 0,
                width: DESIGN_WIDTH,
                height: DESIGN_HEIGHT,
            },
            metrics,
        ),
    )

    drawCoverImage(
        ctx,
        assets.cgImage,
        scaleRect(
            {
                x: CG_RECT.x + CG_PADDING,
                y: CG_RECT.y + CG_PADDING,
                width: CG_RECT.width - CG_PADDING * 2,
                height: CG_RECT.height - CG_PADDING * 2,
            },
            metrics,
        ),
    )
    drawRectImage(ctx, assets.shareFrame, scaleRect(CG_RECT, metrics))
    drawRoundedImage(
        ctx,
        assets.qrCodeImage,
        scaleRect(QRCODE_RECT, metrics),
        scaleValue(8, metrics),
    )

    drawStrokeText(ctx, metrics, {
        text: options.nickname,
        rect: NICKNAME_RECT,
        fontSize: 30,
        fontFamily: FONT_SANS,
        fontWeight: "bold",
        fillColor: "#6c461d",
        strokeColor: "#fffaed",
        strokeWidth: 3,
        minFontSize: 22,
        wrapLines: true,
    })

    if (options.code) {
        drawStrokeText(ctx, metrics, {
            text: options.code,
            rect: CODE_RECT,
            fontSize: 34,
            fontFamily: FONT_NAME,
            fillColor: "#ffffff",
            strokeColor: "#946e0f",
            strokeWidth: 2,
            minFontSize: 26,
        })
    }

    return {
        canvas,
        base64: canvas.toDataURL("image/png"),
    }
}
